/*
 * bnz-cart-drawer.js — Side cart Mimétique (sections/bnz-cart-drawer.liquid)
 * ─────────────────────────────────────────────────────────────────────────────
 * Le rendu est 100 % serveur : chaque action panier renvoie la section via le
 * paramètre `sections` de l'Ajax API, et on remplace [data-bnz-cart-inner].
 * La section calcule aussi les corrections cadeaux (bloc [data-bnz-cart-state]) ;
 * ce script se contente de les exécuter (3 passes max pour éviter toute boucle).
 *
 * Intégration au thème WeTheme (theme.min.js non modifié) :
 *   - événement `wetheme-toggle-right-drawer` type "cart" intercepté en capture
 *   - eventBus.emit('toggle:right:drawer', { type: 'cart' }) détourné
 *   - clic sur l'icône panier du header
 *   - tout fetch / XHR vers /cart/add|change|update|clear fait par un autre
 *     script (formulaires produit, quick add, apps) → rafraîchit (et ouvre si ajout)
 *
 * API publique : window.BnzCart.open() / close() / refresh({ open })
 */
(() => {
  if (customElements.get('bnz-cart-drawer')) return;

  const OWN_HEADER = 'X-Bnz-Cart';
  const CART_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const MAX_SYNC_PASSES = 3;
  const nativeFetch = window.fetch.bind(window);

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const instance = () => window.BnzCart || null;

  class BnzCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.onCartPage = this.hasAttribute('data-on-cart-page');
      this.recoCache = new Map();
      this.openChoices = new Set();
      this.syncPasses = 0;
      this.queue = Promise.resolve();

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      // `toggle` ne remonte pas : écoute en capture pour mémoriser les choix dépliés
      this.addEventListener('toggle', (e) => {
        const id = e.target.dataset && e.target.dataset.bnzChoice;
        if (!id) return;
        if (e.target.open) {
          this.openChoices.add(id);
          if (!e.target.dataset.restored) {
            e.target.classList.add('is-opening');
            setTimeout(() => e.target.classList.remove('is-opening'), 300);
          }
        } else {
          this.openChoices.delete(id);
        }
        delete e.target.dataset.restored;
      }, true);

      window.BnzCart = this;
      this.afterRender();
      this.handleShareLink();
      this.handleOpenLink();
    }

    disconnectedCallback() {
      if (window.BnzCart === this) window.BnzCart = null;
      document.body.classList.remove('bnz-cart-open');
    }

    /* ─── Ouverture ─────────────────────────────────────────────────────── */

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      if (this.isOpen) return;
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('bnz-cart-open');
      const panel = this.querySelector('.bnz-cart__panel');
      requestAnimationFrame(() => panel && panel.focus({ preventScroll: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.body.classList.remove('bnz-cart-open');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    /* ─── Requêtes ──────────────────────────────────────────────────────── */

    // Sérialise les appels : deux clics rapides ne doivent pas se croiser.
    enqueue(task) {
      const run = this.queue.then(task);
      this.queue = run.catch((err) => console.warn('[bnz-cart]', err.message));
      return this.queue;
    }

    async post(path, body) {
      const res = await nativeFetch(`${root()}${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_HEADER]: '1' },
        body: JSON.stringify({ ...body, sections: [this.sectionId], sections_url: window.location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Erreur panier');
      return data;
    }

    async refresh({ open = false } = {}) {
      return this.enqueue(async () => {
        const res = await nativeFetch(`${root()}?sections=${encodeURIComponent(this.sectionId)}`, {
          credentials: 'same-origin',
          headers: { [OWN_HEADER]: '1' },
        });
        if (!res.ok) return;
        const data = await res.json();
        this.render(data[this.sectionId]);
        if (open) this.open();
      });
    }

    // Action utilisateur : on repart de zéro pour les passes de synchro cadeaux.
    act(fn) {
      this.syncPasses = 0;
      return this.enqueue(async () => {
        this.classList.add('is-busy');
        try {
          const data = await fn();
          if (data && data.sections) this.render(data.sections[this.sectionId]);
        } catch (err) {
          this.showError(err.message);
        } finally {
          this.classList.remove('is-busy');
        }
      });
    }

    changeLine(key, quantity) {
      return this.act(() => this.post('cart/change.js', { id: key, quantity }));
    }

    addItems(items) {
      return this.act(() => this.post('cart/add.js', { items }));
    }

    /* ─── Codes promo ───────────────────────────────────────────────────── */

    // `discount` remplace TOUS les codes du panier : on renvoie la liste complète
    // (chaîne vide = plus aucun code). data-codes ne liste que les codes applicables :
    // le `cart.discount_codes` Liquid omet les codes refusés (le JSON, lui, les garde).
    promoCodes() {
      const box = this.querySelector('[data-bnz-promo]');
      return ((box && box.dataset.codes) || '').split(',').filter(Boolean);
    }

    setPromoCodes(codes) {
      return this.act(() => this.post('cart/update.js', { discount: codes.join(',') }));
    }

    // Ajoute un code et vérifie la réponse JSON : un code refusé y figure avec
    // applicable = false mais n'apparaît pas au rendu Liquid. On le retire alors du
    // panier (sinon il traîne jusqu'au checkout) et on affiche le message après rendu.
    addPromoCode(code) {
      const codes = this.promoCodes();
      return this.act(async () => {
        const data = await this.post('cart/update.js', { discount: [...codes, code].join(',') });
        const hit = (data.discount_codes || []).find((c) => c.code.toLowerCase() === code.toLowerCase());
        if (hit && hit.applicable) return data;
        this.promoError = code;
        return this.post('cart/update.js', { discount: codes.join(',') });
      });
    }

    showPromoError() {
      const code = this.promoError;
      this.promoError = null;
      const box = this.querySelector('[data-bnz-promo]');
      if (!code || !box) return;
      const input = box.querySelector('input[name="discount"]');
      if (input) {
        input.value = code; // le client corrige sa saisie au lieu de tout retaper
        input.setAttribute('aria-invalid', 'true');
      }
      const msg = document.createElement('p');
      msg.className = 'bnz-cart__promo-msg';
      msg.setAttribute('role', 'alert');
      msg.textContent = (box.dataset.invalidText || '').replace('[code]', code);
      box.append(msg);
    }

    applyPromo(form) {
      const input = form.querySelector('input[name="discount"]');
      const code = input.value.trim();
      if (!code) return input.focus();
      if (this.promoCodes().some((c) => c.toLowerCase() === code.toLowerCase())) {
        input.value = '';
        return null;
      }
      return this.addPromoCode(code);
    }

    /* ─── Rédemption des points (widget Yotpo « Coupons redemption ») ──── */

    // Pas d'API JS Yotpo sur le site (swellAPI absent). Le widget officiel tourne hors
    // écran, dans un conteneur hors de l'inner (il survit donc aux rendus) : on y lit le
    // solde et les paliers pour afficher notre propre UI, et on lui transmet les clics.
    // Le code qu'il génère (.yotpo-copy-code-text) est appliqué au panier automatiquement.
    mountRedeem() {
      const slot = this.querySelector('[data-bnz-loyalty-slot]');
      if (!slot || this.redeemHost) return;
      this.redeemHost = document.createElement('div');
      this.redeemHost.className = 'bnz-cart__yotpo-host';
      this.redeemHost.setAttribute('aria-hidden', 'true');
      const widget = document.createElement('div');
      widget.className = 'yotpo-widget-instance';
      widget.dataset.yotpoInstanceId = slot.dataset.instanceId;
      this.redeemHost.append(widget);
      this.append(this.redeemHost);
      this.redeemCodes = new Set();
      new MutationObserver(() => {
        cancelAnimationFrame(this.redeemFrame);
        this.redeemFrame = requestAnimationFrame(() => this.syncRedeem());
      }).observe(this.redeemHost, { childList: true, subtree: true, characterData: true });
      this.initYotpoWidgets();
      // Widget muet au bout de 8 s : lien de repli vers la page du programme
      setTimeout(() => {
        this.redeemTimedOut = true;
        this.syncRedeem();
      }, 8000);
    }

    // Le loader Yotpo est asynchrone : on attend yotpoWidgetsContainer (5 s max).
    initYotpoWidgets(tries = 0) {
      const container = window.yotpoWidgetsContainer;
      if (container && typeof container.initWidgets === 'function') return container.initWidgets();
      if (tries < 25) setTimeout(() => this.initYotpoWidgets(tries + 1), 200);
      return null;
    }

    readRedeem() {
      const host = this.redeemHost;
      const text = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');
      const balance = text(host.querySelector('.yotpo-points-balance-text-part.notranslate'));
      const options = new Map(); // dédoublonne les listes desktop / mobile / carrousel
      host.querySelectorAll('.yotpo-redemption-item').forEach((item) => {
        const reward = text(item.querySelector('.yotpo-redemption-option-reward'));
        if (!reward || options.has(reward)) return;
        const cost = text(item.querySelector('.yotpo-redemption-option-cost'));
        options.set(reward, {
          reward,
          cost,
          item,
          costValue: Number(cost.replace(/\D/g, '')) || 0,
          code: text(item.querySelector('.yotpo-copy-code-text')),
        });
      });
      return {
        balance,
        balanceValue: balance ? Number(balance.replace(/\D/g, '')) : null,
        rule: text(host.querySelector('.yotpo-rule')),
        options: [...options.values()],
      };
    }

    syncRedeem() {
      if (!this.redeemHost) return;
      const state = this.readRedeem();

      state.options.forEach(({ code }) => {
        if (!code || this.redeemCodes.has(code)) return;
        this.redeemCodes.add(code);
        this.redeemBusy = null;
        if (!this.promoCodes().some((c) => c.toLowerCase() === code.toLowerCase())) this.addPromoCode(code);
      });

      const toggle = this.querySelector('[data-bnz-loyalty-toggle]');
      if (toggle && state.balance) toggle.textContent = toggle.dataset.label.replace('[points]', `${state.balance} pts`);

      const slot = this.querySelector('[data-bnz-loyalty-slot]');
      if (!slot) return;
      const balance = slot.querySelector('[data-bnz-loyalty-balance]');
      if (balance && state.balance) balance.textContent = state.balance;
      const rule = slot.querySelector('[data-bnz-loyalty-rule]');
      if (rule) rule.textContent = state.rule;
      this.renderRedeemOptions(slot, state);
    }

    renderRedeemOptions(slot, state) {
      const list = slot.querySelector('[data-bnz-loyalty-options]');
      if (!list) return;
      const t = slot.dataset;
      const el = (tag, className, text) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text) node.textContent = text;
        return node;
      };

      if (!state.options.length) {
        if (!this.redeemTimedOut) return;
        const link = el('a', 'bnz-cart__redeem-fallback', t.textFallback);
        link.href = t.fallbackUrl;
        list.replaceChildren(link);
        return;
      }

      const pending = state.options.find((o) => o.reward === this.redeemConfirm);
      if (pending) {
        const box = el('div', 'bnz-cart__redeem-confirm');
        box.append(
          el('p', 'bnz-cart__redeem-question', t.textConfirm.replace('[cost]', pending.cost).replace('[reward]', pending.reward)),
          Object.assign(el('button', 'bnz-cart__pill bnz-cart__redeem-yes', t.textYes), { type: 'button' }),
          Object.assign(el('button', 'bnz-cart__pill', t.textNo), { type: 'button' }),
        );
        box.children[1].dataset.bnzRedeemConfirm = '';
        box.children[2].dataset.bnzRedeemCancel = '';
        list.replaceChildren(box);
        return;
      }

      list.replaceChildren(...state.options.map((o) => {
        const chip = el(o.code ? 'span' : 'button', 'bnz-cart__redeem-chip');
        chip.append(el('span', 'bnz-cart__redeem-reward', o.reward));
        if (o.code) {
          chip.classList.add('is-applied');
          chip.append(el('span', 'bnz-cart__redeem-cost', t.textApplied));
          return chip;
        }
        chip.type = 'button';
        chip.dataset.bnzRedeem = o.reward;
        const busy = this.redeemBusy === o.reward;
        chip.append(el('span', 'bnz-cart__redeem-cost', busy ? t.textBusy : o.cost));
        chip.disabled = Boolean(this.redeemBusy) || (state.balanceValue !== null && o.costValue > state.balanceValue);
        return chip;
      }));
    }

    placeRedeem() {
      const slot = this.querySelector('[data-bnz-loyalty-slot]');
      const toggle = this.querySelector('[data-bnz-loyalty-toggle]');
      if (!slot) return;
      if (toggle) toggle.setAttribute('aria-expanded', String(Boolean(this.redeemOpen)));
      slot.hidden = !this.redeemOpen;
    }

    // Rejoue dans le widget caché : clic « Utiliser » du palier, puis « Oui » de sa
    // confirmation. Le code arrive ensuite via le MutationObserver (syncRedeem).
    async redeem(reward) {
      this.redeemConfirm = null;
      const option = this.readRedeem().options.find((o) => o.reward === reward);
      if (!option) return this.syncRedeem();
      this.redeemBusy = reward;
      this.syncRedeem();
      const button = option.item.querySelector('.yotpo-redemption-option-redeem-button');
      if (button) button.click();
      const confirm = await this.waitFor(() => option.item.querySelector('.yotpo-redemption-confirmation-option-confirm-btn'), 3000);
      if (confirm) confirm.click();
      setTimeout(() => {
        if (this.redeemBusy !== reward) return;
        this.redeemBusy = null;
        this.syncRedeem();
        const slot = this.querySelector('[data-bnz-loyalty-slot]');
        if (slot) this.showError(slot.dataset.textError);
      }, 15000);
      return null;
    }

    waitFor(find, timeout) {
      const start = Date.now();
      return new Promise((resolve) => {
        const tick = () => {
          const found = find();
          if (found || Date.now() - start > timeout) return resolve(found || null);
          return setTimeout(tick, 100);
        };
        tick();
      });
    }

    /* ─── Lien de partage : ?add=ID:QTE,ID:QTE&promo=CODE ──────────────── */

    // Ajoute les variantes au panier existant (sans le remplacer, contrairement
    // aux cart permalinks /cart/ID:QTE qui partent au checkout), applique le code,
    // puis ouvre le drawer. L'URL est nettoyée d'abord : un rechargement ne doit
    // pas ré-ajouter les produits.
    async handleShareLink() {
      const params = new URLSearchParams(window.location.search);
      const add = params.get('add') || '';
      const promo = (params.get('promo') || '').trim();
      if (!add && !promo) return;

      params.delete('add');
      params.delete('promo');
      const query = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);

      const items = add.split(',').map((part) => {
        const [id, qty] = part.split(':');
        return { id: Number(id), quantity: Math.max(1, parseInt(qty, 10) || 1) };
      }).filter((item) => item.id > 0);

      if (items.length) await this.addItems(items);
      if (promo) {
        const codes = this.promoCodes();
        if (!codes.some((c) => c.toLowerCase() === promo.toLowerCase())) await this.addPromoCode(promo);
      }

      // La page /cart est rendue par le template, pas par le drawer
      if (this.onCartPage) window.location.reload();
      else this.open();
    }

    /* ─── Lien d'ouverture : ?cart=open (emails panier abandonné) ──────── */

    // Seul le paramètre `cart` est retiré : utm_* et _kx (identification
    // Klaviyo) restent dans l'URL pour les scripts de tracking. Sur /cart, le
    // drawer est inactif : on bascule vers l'accueil avec la même query.
    handleOpenLink() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('cart') !== 'open') return;

      if (this.onCartPage) {
        window.location.replace(`${root()}${window.location.search}${window.location.hash}`);
        return;
      }

      params.delete('cart');
      const query = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      this.refresh({ open: true });
    }

    /* ─── Rendu ─────────────────────────────────────────────────────────── */

    render(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-bnz-cart-inner]');
      const current = this.querySelector('[data-bnz-cart-inner]');
      if (!next || !current) return;

      const scroller = this.querySelector('[data-bnz-cart-scroll]');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      current.innerHTML = next.innerHTML;
      this.openChoices.forEach((id) => {
        const details = this.querySelector(`[data-bnz-choice="${id}"]`);
        if (details) {
          details.dataset.restored = '1'; // réouverture après rendu : pas d'animation
          details.open = true;
        }
      });
      const newScroller = this.querySelector('[data-bnz-cart-scroll]');
      if (newScroller) newScroller.scrollTop = scrollTop;

      this.afterRender();
    }

    afterRender() {
      const state = this.readState();
      this.updateCountBubbles(state.itemCount);
      this.loadRecommendations();
      this.showPromoError();
      this.mountRedeem();
      this.placeRedeem();
      this.syncRedeem();
      document.dispatchEvent(new CustomEvent('bnz:cart:rendered', { detail: state }));
      this.syncGifts(state);
    }

    readState() {
      const el = this.querySelector('[data-bnz-cart-state]');
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return { itemCount: 0, remove: [], fix: [], add: [] };
      }
    }

    updateCountBubbles(count) {
      if (typeof count !== 'number') return;
      document.querySelectorAll('[data-cart-count-indicator]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('hide', count === 0);
      });
    }

    /* ─── Cadeaux : exécute les corrections calculées par la section ─────── */

    syncGifts(state) {
      const remove = (state.remove || []).filter(Boolean);
      const fix = (state.fix || []).filter(Boolean);
      const add = (state.add || []).filter(Boolean);
      if (!remove.length && !fix.length && !add.length) {
        this.syncPasses = 0;
        return;
      }
      if (this.syncPasses >= MAX_SYNC_PASSES) return;
      this.syncPasses += 1;

      this.enqueue(async () => {
        try {
          let data = null;
          if (remove.length || fix.length) {
            const updates = {};
            remove.forEach((key) => { updates[key] = 0; });
            fix.forEach((key) => { updates[key] = 1; });
            data = await this.post('cart/update.js', { updates });
          }
          if (add.length) {
            const items = add.map((entry) => {
              const [id, tier] = entry.split(':');
              return { id: Number(id), quantity: 1, properties: { _bnz_gift: tier } };
            });
            data = await this.post('cart/add.js', { items });
          }
          if (data && data.sections) this.render(data.sections[this.sectionId]);
        } catch (err) {
          console.warn('[bnz-cart] synchro cadeaux :', err.message);
        }
      });
    }

    /* ─── Recommandations ───────────────────────────────────────────────── */

    async loadRecommendations() {
      const box = this.querySelector('[data-bnz-reco]');
      if (!box) return;
      const list = box.querySelector('[data-bnz-reco-list]');
      const productId = box.dataset.productId;
      const limit = Number(this.dataset.recoLimit) || 4;
      const toggle = () => { box.hidden = !list.querySelector('li'); };

      if (this.dataset.recoSource !== 'auto' || !productId) return toggle();

      // La section exclut les produits du panier : la clé de cache dépend donc du panier.
      const cartKey = [...this.querySelectorAll('.bnz-cart__item')].map((li) => li.dataset.key).join(',');
      const cacheKey = `${productId}|${cartKey}`;

      if (!this.recoCache.has(cacheKey)) {
        this.recoCache.set(cacheKey, this.fetchRecommendations(productId, limit));
      }
      toggle();
      const html = await this.recoCache.get(cacheKey);
      const liveList = this.querySelector('[data-bnz-reco-list]');
      if (html && liveList) {
        liveList.innerHTML = html;
        liveList.closest('[data-bnz-reco]').hidden = false;
      }
    }

    async fetchRecommendations(productId, limit) {
      for (const intent of ['complementary', 'related']) {
        try {
          const url = `${root()}recommendations/products?section_id=bnz-cart-recommendations&product_id=${productId}&limit=10&intent=${intent}`;
          const res = await nativeFetch(url, { headers: { [OWN_HEADER]: '1' } });
          if (!res.ok) continue;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const items = [...doc.querySelectorAll('[data-bnz-reco-result] > li')].slice(0, limit);
          if (items.length) return items.map((li) => li.outerHTML).join('');
        } catch (e) {
          /* on garde la liste manuelle */
        }
      }
      return null;
    }

    /* ─── Événements ────────────────────────────────────────────────────── */

    onClick(e) {
      const t = e.target;

      if (t.closest('[data-bnz-cart-close]')) {
        e.preventDefault();
        return this.close();
      }

      const step = t.closest('[data-bnz-qty-step]');
      if (step) {
        const li = step.closest('[data-key]');
        const input = li.querySelector('[data-bnz-qty-input]');
        const qty = Math.max(0, Number(input.value) + Number(step.dataset.bnzQtyStep));
        return this.changeLine(li.dataset.key, qty);
      }

      const remove = t.closest('[data-bnz-remove]');
      if (remove) return this.changeLine(remove.closest('[data-key]').dataset.key, 0);

      const giftAdd = t.closest('[data-bnz-gift-add]');
      if (giftAdd) return this.pickGift(giftAdd);

      const giftRemove = t.closest('[data-bnz-gift-remove]');
      if (giftRemove) {
        const tier = giftRemove.dataset.tier;
        const li = this.querySelector(`.bnz-cart__item[data-gift-tier="${tier}"][data-variant-id="${giftRemove.dataset.bnzGiftRemove}"]`);
        if (li) return this.changeLine(li.dataset.key, 0);
      }

      if (t.closest('[data-bnz-loyalty-toggle]')) {
        this.redeemOpen = !this.redeemOpen;
        return this.placeRedeem();
      }
      const redeemPick = t.closest('[data-bnz-redeem]');
      if (redeemPick) {
        this.redeemConfirm = redeemPick.dataset.bnzRedeem;
        return this.syncRedeem();
      }
      if (t.closest('[data-bnz-redeem-cancel]')) {
        this.redeemConfirm = null;
        return this.syncRedeem();
      }
      if (t.closest('[data-bnz-redeem-confirm]')) return this.redeem(this.redeemConfirm);

      const promoRemove = t.closest('[data-bnz-promo-remove]');
      if (promoRemove) {
        const code = promoRemove.dataset.bnzPromoRemove;
        return this.setPromoCodes(this.promoCodes().filter((c) => c !== code));
      }

      const recoAdd = t.closest('[data-bnz-reco-add]');
      if (recoAdd) {
        recoAdd.disabled = true;
        return this.addItems([{ id: Number(recoAdd.dataset.bnzRecoAdd), quantity: 1, properties: { _source: 'side-cart-reco' } }]);
      }
    }

    // Choix d'un échantillon : avec 1 seul choix autorisé, le nouveau remplace l'ancien.
    pickGift(button) {
      const tier = button.dataset.tier;
      const box = button.closest('[data-bnz-choice]');
      const max = Number(box && box.dataset.max) || 1;
      const picked = [...this.querySelectorAll(`.bnz-cart__item[data-gift-tier="${tier}"]`)];

      this.act(async () => {
        if (picked.length >= max) {
          if (max > 1) return null;
          const updates = {};
          picked.forEach((li) => { updates[li.dataset.key] = 0; });
          await this.post('cart/update.js', { updates });
        }
        return this.post('cart/add.js', {
          items: [{ id: Number(button.dataset.bnzGiftAdd), quantity: 1, properties: { _bnz_gift: tier } }],
        });
      });
    }

    onChange(e) {
      const wrap = e.target.closest('[data-bnz-giftwrap]');
      if (wrap) {
        if (!wrap.checked && wrap.dataset.key) return this.changeLine(wrap.dataset.key, 0);
        if (wrap.checked) {
          return this.addItems([{ id: Number(wrap.dataset.bnzGiftwrap), quantity: 1, properties: { _bnz_giftwrap: '1' } }]);
        }
        return;
      }

      const input = e.target.closest('[data-bnz-qty-input]');
      if (!input) return;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      this.changeLine(input.closest('[data-key]').dataset.key, qty);
    }

    onSubmit(e) {
      const form = e.target.closest('[data-bnz-promo-form]');
      if (!form) return;
      e.preventDefault();
      this.applyPromo(form);
    }

    onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') return this.close();
      if (e.key !== 'Tab') return;
      // Piège de focus dans le panneau
      const focusables = [...this.querySelectorAll('.bnz-cart__panel a[href], .bnz-cart__panel button:not([disabled]), .bnz-cart__panel input')]
        .filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.querySelector('.bnz-cart__panel'))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    showError(message) {
      const body = this.querySelector('[data-bnz-cart-scroll]');
      if (!body) return;
      const el = document.createElement('p');
      el.className = 'bnz-cart__error';
      el.setAttribute('role', 'alert');
      el.textContent = message;
      body.prepend(el);
      body.scrollTop = 0;
      setTimeout(() => el.remove(), 5000);
    }

    // Appelé par les hooks du thème (événement WeTheme / eventBus)
    handleToggle(detail = {}) {
      if (detail.forceOpen === false) return this.close();
      if (detail.forceOpen !== true && this.isOpen) return this.close();
      this.refresh();
      this.open();
    }
  }

  customElements.define('bnz-cart-drawer', BnzCartDrawer);

  /* ─── Hooks thème ─────────────────────────────────────────────────────── */

  // 1. theme.toggleRightDrawer('cart', …) dispatche sur <html> : on passe avant en capture sur window.
  window.addEventListener('wetheme-toggle-right-drawer', (e) => {
    const cart = instance();
    if (!cart || !e.detail || e.detail.type !== 'cart') return;
    e.stopImmediatePropagation();
    cart.handleToggle(e.detail);
  }, true);

  // 2. Web components du thème : eventBus.emit('toggle:right:drawer', { type: 'cart' })
  const patchEventBus = () => {
    const bus = window.eventBus;
    if (!bus || bus.__bnzCart) return;
    const emit = bus.emit.bind(bus);
    bus.emit = (event, data) => {
      const cart = instance();
      if (event === 'toggle:right:drawer' && data && data.type === 'cart' && cart) return cart.handleToggle(data);
      return emit(event, data);
    };
    bus.__bnzCart = true;
  };
  patchEventBus();
  document.addEventListener('DOMContentLoaded', patchEventBus);

  // 3. Icône panier du header
  document.addEventListener('click', (e) => {
    const cart = instance();
    if (!cart || cart.onCartPage || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
    const trigger = e.target.closest('.site-header__cart-toggle, .js-drawer-open-right-link, [data-bnz-cart-open]');
    if (!trigger || cart.contains(trigger)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    cart.refresh();
    cart.open();
  }, true);

  // 4. Requêtes panier faites par d'autres scripts → rafraîchir (et ouvrir si ajout)
  let pending = null;
  const onExternalCartChange = (url) => {
    const cart = instance();
    if (!cart) return;
    const isAdd = /\/cart\/add/.test(url);
    pending = { open: (pending && pending.open) || (isAdd && !cart.onCartPage) };
    clearTimeout(onExternalCartChange.timer);
    onExternalCartChange.timer = setTimeout(() => {
      const opts = pending;
      pending = null;
      cart.syncPasses = 0;
      cart.refresh(opts);
    }, 120);
  };

  const hasOwnHeader = (init, input) => {
    const headers = (init && init.headers) || (input instanceof Request ? input.headers : null);
    if (!headers) return false;
    if (headers instanceof Headers) return headers.has(OWN_HEADER);
    return Object.keys(headers).some((k) => k.toLowerCase() === OWN_HEADER.toLowerCase());
  };

  window.fetch = function bnzFetch(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const promise = nativeFetch(input, init);
    if (CART_RE.test(url) && !hasOwnHeader(init, input)) {
      promise.then((res) => { if (res.ok) onExternalCartChange(url); }).catch(() => {});
    }
    return promise;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function bnzXhrOpen(method, url, ...rest) {
    if (CART_RE.test(String(url))) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) onExternalCartChange(String(url));
      });
    }
    return xhrOpen.call(this, method, url, ...rest);
  };

  // 5. Éditeur de thème : ouvrir le panier quand on sélectionne la section ou un palier
  if (window.Shopify && window.Shopify.designMode) {
    const isOurs = (e) => { const c = instance(); return c && e.detail && e.detail.sectionId === c.sectionId; };
    document.addEventListener('shopify:section:select', (e) => isOurs(e) && instance().open());
    document.addEventListener('shopify:block:select', (e) => isOurs(e) && instance().open());
    document.addEventListener('shopify:section:deselect', (e) => isOurs(e) && instance().close());
  }
})();
